import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

import style from "./index.module.css";

import { styles } from "@/utils/styles";
import {
  doctorDegreeName,
  doctorSpeakLanguages,
  getDateTime,
  transformClasses,
} from "@/utils";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

import Button from "@/components/Button";
import Banner from "@/components/Banner";
import GetImages from "@/components/GetImages";
import ContactForm from "@/components/ContactForm";
import GenericCardHeader from "@/components/GenericCardHeader";
import MakeAppointment from "@/components/FindDoctorCard/MakeAppointment";
import Dialog from "@/components/Dialog";

import { IButtonVariant } from "@/models/button";
import {
  IAppointmentCreation,
  IAppointmetEnums,
  IFindDoctorAvailabilityPlatform,
  IDoctorProfileStatus,
  IMakeAppointmentEvent,
  IDoctorAvailabilityPlatform,
} from "@/models/doctor";
import { IQualificationDetail } from "@/models/ApiRequestResponse/patient";
import { IRedirectAfterLoginType } from "@/models";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { checkoutDetail, findDoctor } from "@/store/slices/patient/findDoctor";
import {
  config,
  handleIsShowSuccessModal,
  setIsRequestCallBackOpen,
} from "@/store/slices/config";
import { fetchDoctorProfile } from "@/store/slices/doctor/profile";

export interface IVideo {
  time?: string;
  price?: string;
}

export interface IClinic {
  time?: string;
  price?: string;
}
export interface ITimePrice {
  video: IVideo;
  clinic: IClinic;
}

interface IProps {
  doctor_id: string;
  doctor_name: string;
  year_of_experience: number;
  speciality: string;
  qualification_details: IQualificationDetail[];
  language: string[];
  doctor_photo_url: string;
  distance: number;
  clinic_address: string;
  clinic_name: string;
  reviews: number;
  expertise: string;
  type: IFindDoctorAvailabilityPlatform;
  video_consultation_fee: number;
  next_video_consultation_slot: string;
  in_clinic_consultation_fee: number;
  next_in_clinic_slot: string;
  currentIndex: number;
  showContactForm?: boolean;
  contact_support: string;
  profile_tags: string[];
}

const platformMap: any = {
  [IFindDoctorAvailabilityPlatform.VIDEO_CALL]:
    IDoctorAvailabilityPlatform.VIDEO_CALL,
  [IFindDoctorAvailabilityPlatform.IN_CLINIC]:
    IDoctorAvailabilityPlatform.IN_CLINIC,
};

const FindDoctorCard: React.FC<IProps> = (props) => {
  const {
    doctor_id,
    doctor_name,
    year_of_experience,
    speciality,
    qualification_details,
    language,
    doctor_photo_url,
    distance,
    clinic_address,
    clinic_name,
    reviews,
    expertise,
    type,
    video_consultation_fee,
    next_video_consultation_slot,
    in_clinic_consultation_fee,
    next_in_clinic_slot,
    currentIndex,
    showContactForm = true,
    contact_support,
    profile_tags,
  } = props;

  const [isAppointmentCreation, setIsAppointmentCreation] =
    useState<IAppointmentCreation>({
      creation: false,
      success: false,
    });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [platform, setPlatform] = useState<IDoctorAvailabilityPlatform>();

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isSmallScreen = useMemo(
    () => isMobile || isTablet,
    [isMobile, isTablet]
  );

  const intl = useIntl();

  const dispatch = useAppDispatch();
  const { isShowUniversalSearch, isShowSuccessModal, redirectAfterLogin } =
    useAppSelector(config);
  const { checkoutPatientDetail } = useAppSelector(findDoctor);

  const closeAppointmentModal = () => {
    const schedulesValues: any = {};
    for (let key in isAppointmentCreation) {
      schedulesValues[key] = false;
    }

    if (isShowSuccessModal) {
      dispatch(handleIsShowSuccessModal());
    }
    setIsAppointmentCreation(schedulesValues);
  };

  const openAppointmentModalHandler = (type: IAppointmetEnums) => {
    const schedulesValues: any = {};
    for (let key in isAppointmentCreation) {
      schedulesValues[key] = false;
    }
    switch (type) {
      case IAppointmetEnums.CREATION:
        setIsAppointmentCreation({
          ...schedulesValues,
          creation: true,
        });
        return;
      case IAppointmetEnums.SUCCESS:
        setIsAppointmentCreation({
          ...schedulesValues,
          success: true,
        });
        return;
      default:
        closeAppointmentModal();
        return;
    }
  };

  const selectedTabOpenAppointmentModalHandler = (
    type: IAppointmetEnums,
    tab: IMakeAppointmentEvent,
    doctorPlatform: IDoctorAvailabilityPlatform
  ) => {
    dispatch(fetchDoctorProfile(doctor_id));
    dispatch(
      checkoutDetail({
        doctorId: doctor_id,
        doctorImage: doctor_photo_url,
        doctorName: doctor_name,
        bio: `${year_of_experience} yrs • ${speciality} • ${doctorDegreeName(
          qualification_details
        )}`,
        languages: doctorSpeakLanguages(language),
        type:
          tab === 0
            ? IDoctorAvailabilityPlatform.VIDEO_CALL
            : IDoctorAvailabilityPlatform.IN_CLINIC,
      })
    );
    setSelectedTab(tab);
    setPlatform(doctorPlatform);
    openAppointmentModalHandler(type);
  };

  useEffect(() => {
    if (
      doctor_id === checkoutPatientDetail?.doctorId &&
      (isMobile || isTablet) &&
      redirectAfterLogin?.type === IRedirectAfterLoginType.FIND_DOCTOR
    ) {
      setIsAppointmentCreation({
        ...isAppointmentCreation,
        creation: redirectAfterLogin?.isRedirect,
      });
      setSelectedTab(Number(redirectAfterLogin?.selectedTab));
    }
  }, []);

  if (isShowUniversalSearch) {
    return (
      <Grid item className={style.cardContainer}>
        <GenericCardHeader
          doctorId={doctor_id}
          image={doctor_photo_url}
          name={doctor_name}
          bio={`${year_of_experience} yrs • ${speciality} • ${doctorDegreeName(
            qualification_details
          )}`}
          languages={doctorSpeakLanguages(language)}
          status={IDoctorProfileStatus.VERIFIED}
        />
        {/* Address */}
        <Grid item className={style.addressContainer}>
          <GetImages name="LocationIcon" width="16" height="16" />
          <Typography component={"p"} className={style.addressText}>
            {Math.round(distance)}km • {clinic_name} • {clinic_address}
          </Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <>
      <Grid item className={style.cardContainer}>
        <Grid item className={style.doctorCardAvailabilityOptionContainer}>
          <Grid item className={style.doctorDetailContainer}>
            <GenericCardHeader
              doctorId={doctor_id}
              image={doctor_photo_url}
              name={doctor_name}
              bio={`${year_of_experience} yrs • ${speciality} • ${doctorDegreeName(
                qualification_details
              )}`}
              languages={doctorSpeakLanguages(language)}
              status={IDoctorProfileStatus.VERIFIED}
              profileTags={profile_tags}
            />
            {/* Address */}
            <Grid item className={style.addressContainer}>
              <GetImages name="LocationIcon" width="16" height="16" />
              <Typography component={"p"} className={style.addressText}>
                {distance && distance?.toFixed(2)}km{" "}
                {clinic_name && `• ${clinic_name}`}{" "}
                {clinic_address && `• ${clinic_address}`}
              </Typography>
            </Grid>
          </Grid>
          {/* Availability Options */}
          <Grid item className={style.availabilityBtns}>
            {(type === IFindDoctorAvailabilityPlatform.BOTH ||
              type === IFindDoctorAvailabilityPlatform.VIDEO_CALL) && (
              <Grid
                item
                width={"100%"}
                className={
                  type === IFindDoctorAvailabilityPlatform.BOTH
                    ? style.availabilityVideo
                    : ""
                }
              >
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={() =>
                    selectedTabOpenAppointmentModalHandler(
                      IAppointmetEnums.CREATION,
                      IMakeAppointmentEvent.VIDEO_CALL,
                      platformMap[type] || IDoctorAvailabilityPlatform.BOTH
                    )
                  }
                >
                  <GetImages name="VideoIcon" width="20" height="20" />
                  <Typography component={"p"} sx={styles.ml6}>
                    {intl.formatMessage({
                      id: "video_call",
                      defaultMessage: "Video Call",
                    })}
                  </Typography>
                </Button>
                <Typography
                  component={"p"}
                  className={transformClasses(
                    style.doctorPriceTimeLabel,
                    style.doctorTime
                  )}
                >
                  {<GetImages name="GreenDotIcon" width="6" height="7" />}{" "}
                  {next_video_consultation_slot &&
                    getDateTime(next_video_consultation_slot)}
                  {"  "}
                  <Typography
                    component={"span"}
                    className={transformClasses(
                      style.doctorPriceTimeLabel,
                      style.doctorPrice
                    )}
                  >
                    ₹{video_consultation_fee ?? 0}
                  </Typography>
                </Typography>
              </Grid>
            )}
            {(type === IFindDoctorAvailabilityPlatform.BOTH ||
              type === IFindDoctorAvailabilityPlatform.IN_CLINIC) && (
              <Grid item width={"100%"}>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={() =>
                    selectedTabOpenAppointmentModalHandler(
                      IAppointmetEnums.CREATION,
                      IMakeAppointmentEvent.IN_CLINIC,
                      platformMap[type] || IDoctorAvailabilityPlatform.BOTH
                    )
                  }
                >
                  <GetImages name="ClinicIcon" width="20" height="20" />
                  <Typography component={"p"} sx={styles.ml6}>
                    In-Clinic
                  </Typography>
                </Button>
                <Typography
                  component={"p"}
                  className={transformClasses(
                    style.doctorPriceTimeLabel,
                    style.doctorTime
                  )}
                >
                  {<GetImages name="GreenDotIcon" width="6" height="7" />}{" "}
                  {next_in_clinic_slot && getDateTime(next_in_clinic_slot)}
                  {"  "}
                  <Typography
                    component={"span"}
                    className={transformClasses(
                      style.doctorPriceTimeLabel,
                      style.doctorPrice
                    )}
                  >
                    ₹{in_clinic_consultation_fee ?? 0}
                  </Typography>
                </Typography>
              </Grid>
            )}
            {type === IFindDoctorAvailabilityPlatform.NONE && (
              <Grid item width={"100%"}>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={() =>
                    (window.location.href = `tel:${contact_support}`)
                  }
                >
                  <GetImages name="phone20by20red" width="20" height="20" />
                  <Typography component={"p"} sx={styles.ml6}>
                    {isSmallScreen ? (
                      <FormattedMessage
                        id="call_to_book_appointment"
                        defaultMessage="Call to Book Appointment"
                      />
                    ) : (
                      contact_support
                    )}
                  </Typography>
                </Button>
                <Typography
                  component={"p"}
                  sx={{ display: { xs: "none", md: "block" } }}
                  className={style.talkToOurCustomerSupportText}
                >
                  <FormattedMessage
                    id="talk_to_our_customer_support_to_book_appointment"
                    defaultMessage="Talk to our Customer support to Book appointment"
                  />
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        {/* Rating */}
        <Grid
          item
          className={transformClasses(
            type === IFindDoctorAvailabilityPlatform.NONE
              ? style.ratingContainerNone
              : style.ratingContainer
          )}
        >
          {reviews > 0 && (
            <Grid item className={style.ratingHeader}>
              <GetImages name="LikeIcon" width="16" height="16" />
              <Typography
                component={"p"}
                className={style.ratingText}
                sx={styles.ml2}
              >
                {reviews}{" "}
                {intl.formatMessage({
                  id: "reviews",
                  defaultMessage: "Reviews",
                })}
              </Typography>
            </Grid>
          )}
          {type === IFindDoctorAvailabilityPlatform.NONE && isSmallScreen ? (
            <Grid item>
              <Typography component={"p"} className={style.ratingText}>
                <FormattedMessage
                  id="talk_to_our_customer_support_to_book_appointment"
                  defaultMessage="Talk to our Customer support to Book appointment"
                />
              </Typography>
            </Grid>
          ) : (
            <>
              {expertise && (
                <Typography
                  component={"p"}
                  className={transformClasses(
                    style.reviewText,
                    style.ratingText
                  )}
                >
                  {expertise}
                </Typography>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {showContactForm && (
        <>
          {isSmallScreen && currentIndex === 1 && (
            <Grid item className={style.doctorCallbackBanner}>
              <Banner
                title={intl.formatMessage({
                  id: "need_help_in_choosing_best_suitable_doctor",
                  defaultMessage: "Need help in choosing best suitable doctor?",
                })}
                desc={intl.formatMessage({
                  id: "let_us_select_a_doctor_for_you",
                  defaultMessage: "Let us select a doctor for you",
                })}
                onClick={() => dispatch(setIsRequestCallBackOpen())}
              />
            </Grid>
          )}
        </>
      )}
      {showContactForm && (
        <>
          {!isShowUniversalSearch && !isSmallScreen && currentIndex === 1 && (
            <Grid item className={style.doctorCallbackBanner}>
              <ContactForm />
            </Grid>
          )}
        </>
      )}
      {isSmallScreen ? (
        <>
          {isAppointmentCreation.creation && (
            <MakeAppointment
              open={isAppointmentCreation.creation}
              doctorId={doctor_id}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              openScheduleModalHandler={openAppointmentModalHandler}
              onClose={closeAppointmentModal}
              platform={platform}
            />
          )}
        </>
      ) : (
        <Dialog
          closeHandler={closeAppointmentModal}
          isOpen={isAppointmentCreation.creation}
          maxWidth="xs"
          fullWidth
          paperProps={{
            sx: {
              backgroundColor: "var(--neutral-100) !important",
              maxWidth: isShowSuccessModal ? "35.5rem" : "40rem",
              minHeight: "10rem",
              ...(isShowSuccessModal
                ? {
                    borderRadius: "1.2rem",
                  }
                : {}),
            },
          }}
        >
          <MakeAppointment
            open={isAppointmentCreation.creation}
            doctorId={doctor_id}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            openScheduleModalHandler={openAppointmentModalHandler}
            onClose={closeAppointmentModal}
            platform={platform}
          />
        </Dialog>
      )}
    </>
  );
};

export default FindDoctorCard;
